import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import {
  useDeleteCourse,
  useGetCourses,
  useUpdateCourseStatus,
} from "../api-clients";
import useCourseTableColumns from "./useCourseTableColumns";

const CoursesList = () => {
  useSetActiveTab(TAB_NAMES.COURSE_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    error,
    data,
    mutate: updateCourseData,
    isValidating,
  } = useGetCourses({ length, page, search: debouncedSearch });
  const { isRefreshing, handleRefresh } = useManualRefresh(updateCourseData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteCourse,
  } = useDeleteCourse();

  const coursesData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const {
    execute: handleCourseStatusChange,
    isSuccess: isStatusUpdateSuccess,
  } = useUpdateCourseStatus();

  const columns = useCourseTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
    handleCourseStatusChange,
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateCourseData();
      setIsDeleteModalOpen(false);
      successNotification("Course Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateCourseData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  useEffect(() => {
    if (isStatusUpdateSuccess) {
      updateCourseData();
    }
  }, [isStatusUpdateSuccess, updateCourseData]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteCourse(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Course Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(coursesData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Course",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={coursesData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default CoursesList;
